import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Eye } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { saltFormationColors } from '@/config/experiments/chem1131/experiment6.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Preparing Metal Oxides',
    content: 'Metal oxides are compounds formed when metals react with oxygen. CaO (quicklime) is white, while CuO is black due to d-orbital electron transitions in the Cu²⁺ ion.',
    whatHappens: 'Small amounts of calcium oxide (CaO) and copper oxide (CuO) are placed in separate test tubes for comparison.',
    observe: ['CaO is a white powder', 'CuO is a black powder', 'Both are ionic compounds with O2- anions'],
    equation: 'Metal + O2 -> Metal Oxide (formation)'
  },
  2: {
    title: 'Adding Nitric Acid',
    content: 'Metal oxides are basic anhydrides - they react with acids to form salts and water. This is similar to a neutralization reaction, where the oxide ion acts as a base.',
    whatHappens: 'Nitric acid (HNO₃) is added to each tube. The acid provides H+ ions that react with the O2- in the metal oxides.',
    observe: ['Acid is a clear, colorless liquid', 'Use dilute (3 M) acid for safety', 'Add slowly to avoid splashing'],
    equation: 'Metal Oxide + 2HNO₃ -> Metal(NO3)2 + H₂O'
  },
  3: {
    title: 'Shaking the Tubes',
    content: 'Agitation increases the reaction rate by providing better contact between the solid oxide and the acid solution. Dissolution occurs as the oxide reacts with acid.',
    whatHappens: 'Gentle shaking mixes the acid with the oxide, allowing the reaction to proceed. The solid gradually dissolves.',
    observe: ['Solid particles begin to disappear', 'Solution may bubble slightly', 'Mix gently - do not splash']
  },
  4: {
    title: 'Observing the Results',
    content: 'The color of the resulting solution depends on the metal cation. Cu²⁺ ions produce a characteristic blue color due to absorption of red/orange light, while Ca²⁺ is colorless.',
    whatHappens: 'CaO dissolves to form colorless Ca(NO3)2, while CuO dissolves to form blue Cu(NO3)2.',
    observe: ['Ca(NO3)2 solution is colorless', 'Cu(NO3)2 solution is blue', 'Color indicates Cu²⁺ ions present'],
    equation: 'CuO + 2HNO₃ -> Cu(NO3)2 (blue) + H₂O'
  },
  5: {
    title: 'Recording Observations',
    content: 'The reaction of metal oxides with acids is a key method for salt preparation. The products demonstrate that metal oxides are basic and can neutralize acids.',
    whatHappens: 'You record the color differences and confirm that both oxides reacted with the acid to form nitrate salts.',
    observe: ['Both reactions produce salts + water', 'Transition metals often give colored solutions', 'Main group metals usually give colorless solutions'],
    equation: 'CaO + 2HNO₃ -> Ca(NO3)2 + H₂O\nCuO + 2HNO₃ -> Cu(NO3)2 + H₂O'
  }
};

export default function acidOxide() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [oxidesPrepared, setOxidesPrepared] = useState(false);
  const [acidAdded, setAcidAdded] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [shaken, setShaken] = useState(false);
  const [resultsObserved, setResultsObserved] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [caoProgress, setCaoProgress] = useState(0);
  const [cuoProgress, setCuoProgress] = useState(0);
  const id = useId();

  const completedSteps = [oxidesPrepared, acidAdded, shaken, resultsObserved, recorded].filter(Boolean).length;

  const handlePrepareOxides = useCallback(() => {
    if (currentStep === 1 && !oxidesPrepared) {
      setOxidesPrepared(true);
      setCurrentStep(2);
    }
  }, [currentStep, oxidesPrepared]);

  const handleAddAcid = useCallback(() => {
    if (currentStep === 2 && !acidAdded) {
      setAcidAdded(true);
      setCurrentStep(3);
    }
  }, [currentStep, acidAdded]);

  const handleShake = useCallback(() => {
    if (currentStep === 3 && acidAdded && !shaken) {
      setShaking(true);

      // Animate dissolution
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.02;
        setCaoProgress(progress);
        setCuoProgress(progress * 0.8); // CuO dissolves slightly slower
        if (progress >= 1) {
          clearInterval(interval);
          setShaking(false);
          setShaken(true);
          setCurrentStep(4);
        }
      }, 80);
    }
  }, [currentStep, acidAdded, shaken]);

  const handleObserveResults = useCallback(() => {
    if (currentStep === 4 && shaken && !resultsObserved) {
      setResultsObserved(true);
      setCurrentStep(5);
    }
  }, [currentStep, shaken, resultsObserved]);

  const handleRecord = useCallback(() => {
    if (currentStep === 5 && resultsObserved && !recorded) {
      setRecorded(true);
    }
  }, [currentStep, resultsObserved, recorded]);

  // Colors for tubes
  const getCaOColor = () => {
    if (shaken) return 'rgba(255,255,255,0.9)'; // Colorless Ca(NO3)2
    if (caoProgress > 0.5) return 'rgba(245,245,245,0.7)';
    return saltFormationColors.cao;
  };

  const getCuOColor = () => {
    if (shaken) return saltFormationColors.cuNO32; // Blue Cu(NO3)2
    if (cuoProgress > 0.7) return '#3366AA';
    if (cuoProgress > 0.4) return '#2A4A7A';
    return saltFormationColors.cuo;
  };

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part C: Acid + Oxide</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare Oxides', hint: 'Add match-head sized CaO and CuO to tubes', done: oxidesPrepared },
            { id: 2, title: 'Add HNO₃', hint: 'Add acid to each tube', done: acidAdded },
            { id: 3, title: 'Shake Gently', hint: 'Mix the solutions', done: shaken },
            { id: 4, title: 'Observe Results', hint: 'Note the color difference', done: resultsObserved },
            { id: 5, title: 'Record', hint: 'Record observations', done: recorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Results summary */}
        {shaken && (
          <div className="results-summary">
            <h4><Eye size={14} /> Results</h4>
            <div className="result-row">
              <div className="swatch" style={{ background: getCaOColor() }} />
              <span>Ca(NO3)2: Colorless</span>
            </div>
            <div className="result-row">
              <div className="swatch" style={{ background: getCuOColor() }} />
              <span>Cu(NO3)2: Blue</span>
            </div>
          </div>
        )}
      </div>

      <LabBench
        experimentName="Metal Oxide + Acid"
        completedCount={completedSteps}
        totalSteps={5}
        equipment={['Test tubes', 'CaO', 'CuO', '3 M HNO₃']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* Oxide containers */}
            <div className="oxide-containers">
              <div
                className={`oxide-container ${currentStep === 1 && !oxidesPrepared ? 'clickable' : ''}`}
                onClick={handlePrepareOxides}
              >
                <svg viewBox="0 0 40 60" width={35} height={55}>
                  <rect x="8" y="12" width="24" height="35" fill="#444" stroke="#666" strokeWidth="1" rx="2" />
                  <ellipse cx="20" cy="30" rx="10" ry="6" fill={saltFormationColors.cao} />
                  <text x="20" y="55" fontSize="6" fill="#E0E0E0" textAnchor="middle">CaO</text>
                </svg>
              </div>
              <div
                className={`oxide-container ${currentStep === 1 && !oxidesPrepared ? 'clickable' : ''}`}
                onClick={handlePrepareOxides}
              >
                <svg viewBox="0 0 40 60" width={35} height={55}>
                  <rect x="8" y="12" width="24" height="35" fill="#444" stroke="#666" strokeWidth="1" rx="2" />
                  <ellipse cx="20" cy="30" rx="10" ry="6" fill={saltFormationColors.cuo} />
                  <text x="20" y="55" fontSize="6" fill="#E0E0E0" textAnchor="middle">CuO</text>
                </svg>
              </div>
            </div>

            {/* Acid bottle */}
            <div
              className={`equipment-station ${currentStep === 2 && !acidAdded ? 'clickable' : ''}`}
              onClick={handleAddAcid}
            >
              <svg viewBox="0 0 50 80" width={45} height={75}>
                <rect x="12" y="20" width="26" height="50" fill="#FFFACD" stroke="#888" strokeWidth="1" rx="2" />
                <rect x="17" y="10" width="16" height="12" fill="#AA4444" rx="2" />
                <text x="25" y="48" fontSize="6" fill="#333" textAnchor="middle">HNO₃</text>
                <text x="25" y="56" fontSize="5" fill="#666" textAnchor="middle">3 M</text>
                <text x="25" y="78" fontSize="7" fill="#E0E0E0" textAnchor="middle">Acid</text>
              </svg>
            </div>

            {/* Test tubes */}
            <div
              className={`test-tubes ${(currentStep === 3 || currentStep === 4) ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 3 && !shaken) handleShake();
                else if (currentStep === 4 && !resultsObserved) handleObserveResults();
              }}
              style={{ cursor: (currentStep === 3 || currentStep === 4) ? 'pointer' : 'default' }}
            >
              {/* CaO tube */}
              <svg viewBox="0 0 50 120" width={50} height={120}>
                <defs>
                  <linearGradient id={`tube-glass-${id}-1`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                  </linearGradient>
                </defs>

                {/* Tube rim */}
                <ellipse cx="25" cy="12" rx="12" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

                {/* Tube body */}
                <path
                  d="M13 12 L13 85 Q13 100 25 100 Q37 100 37 85 L37 12"
                  fill={`url(#tube-glass-${id}-1)`}
                  stroke="#8BA8C4"
                  strokeWidth="1.5"
                />

                {/* Contents */}
                {oxidesPrepared && (
                  <g>
                    {/* Solid oxide (disappearing) */}
                    <ellipse
                      cx="25"
                      cy="90"
                      rx={10 - caoProgress * 8}
                      ry={4 - caoProgress * 3}
                      fill={saltFormationColors.cao}
                      opacity={1 - caoProgress}
                    />

                    {/* Solution forming */}
                    {acidAdded && (
                      <path
                        d={`M15 ${95 - caoProgress * 40} L15 85 Q15 95 25 95 Q35 95 35 85 L35 ${95 - caoProgress * 40} Z`}
                        fill={getCaOColor()}
                      />
                    )}

                    {/* Dissolution bubbles */}
                    {shaking && caoProgress < 0.8 && (
                      <g>
                        {[...Array(3)].map((_, i) => (
                          <circle
                            key={i}
                            cx={20 + i * 5}
                            cy={80}
                            r={2}
                            fill="rgba(255,255,255,0.5)"
                          >
                            <animate attributeName="cy" values="85;50;85" dur={`${0.8 + i * 0.2}s`} repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.5;0;0.5" dur={`${0.8 + i * 0.2}s`} repeatCount="indefinite" />
                          </circle>
                        ))}
                      </g>
                    )}
                  </g>
                )}

                {/* Glass shine */}
                <line x1="16" y1="20" x2="16" y2="75" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />

                <text x="25" y="115" fontSize="8" fill="#E0E0E0" textAnchor="middle">
                  {reactionComplete ? 'Ca(NO3)2' : 'CaO'}
                </text>
              </svg>

              {/* CuO tube */}
              <svg viewBox="0 0 50 120" width={50} height={120}>
                <defs>
                  <linearGradient id={`tube-glass-${id}-2`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                  </linearGradient>
                </defs>

                {/* Tube rim */}
                <ellipse cx="25" cy="12" rx="12" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

                {/* Tube body */}
                <path
                  d="M13 12 L13 85 Q13 100 25 100 Q37 100 37 85 L37 12"
                  fill={`url(#tube-glass-${id}-2)`}
                  stroke="#8BA8C4"
                  strokeWidth="1.5"
                />

                {/* Contents */}
                {oxidesPrepared && (
                  <g>
                    {/* Solid oxide (disappearing) */}
                    <ellipse
                      cx="25"
                      cy="90"
                      rx={10 - cuoProgress * 8}
                      ry={4 - cuoProgress * 3}
                      fill={saltFormationColors.cuo}
                      opacity={1 - cuoProgress}
                    />

                    {/* Solution forming - BLUE! */}
                    {acidAdded && (
                      <path
                        d={`M15 ${95 - cuoProgress * 40} L15 85 Q15 95 25 95 Q35 95 35 85 L35 ${95 - cuoProgress * 40} Z`}
                        fill={getCuOColor()}
                      >
                        {shaking && (
                          <animate
                            attributeName="fill"
                            values={`${saltFormationColors.cuo};#2A4A7A;#3366AA;${saltFormationColors.cuNO32}`}
                            dur="4s"
                            fill="freeze"
                          />
                        )}
                      </path>
                    )}

                    {/* Dissolution effect */}
                    {shaking && cuoProgress < 0.8 && (
                      <g>
                        {[...Array(3)].map((_, i) => (
                          <circle
                            key={i}
                            cx={20 + i * 5}
                            cy={80}
                            r={2}
                            fill={saltFormationColors.cuNO32}
                            opacity={0.5}
                          >
                            <animate attributeName="cy" values="85;50;85" dur={`${0.9 + i * 0.2}s`} repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.5;0.1;0.5" dur={`${0.9 + i * 0.2}s`} repeatCount="indefinite" />
                          </circle>
                        ))}
                      </g>
                    )}
                  </g>
                )}

                {/* Glass shine */}
                <line x1="16" y1="20" x2="16" y2="75" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />

                <text x="25" y="115" fontSize="8" fill={shaken ? saltFormationColors.cuNO32 : '#E0E0E0'} textAnchor="middle">
                  {shaken ? 'Cu(NO3)2' : 'CuO'}
                </text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !oxidesPrepared && (
              <div className="status-message info">
                <Droplets size={20} />
                Add a match-head sized amount of CaO and CuO to separate test tubes
              </div>
            )}
            {currentStep === 2 && !acidAdded && (
              <div className="status-message info">
                <Droplets size={20} />
                Add about 1 mL of 3 M HNO₃ to each tube
              </div>
            )}
            {currentStep === 3 && acidAdded && !shaken && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click tubes to shake gently and mix
              </div>
            )}
            {shaking && (
              <div className="status-message reaction">
                <FlaskConical size={20} />
                Oxides dissolving... watch the color difference!
              </div>
            )}
            {currentStep === 4 && shaken && !resultsObserved && (
              <div className="status-message info">
                <Eye size={20} />
                Click to observe: CuO gives blue, CaO gives colorless
              </div>
            )}
            {currentStep === 5 && resultsObserved && !recorded && (
              <div
                className="status-message success"
                onClick={handleRecord}
                style={{ cursor: 'pointer' }}
              >
                <Check size={20} />
                Click to record observations
              </div>
            )}
            {recorded && (
              <div className="status-message success">
                <Check size={20} />
                Complete! Metal oxides react with acid to form salts.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Metal Oxide + Acid</h4>
            <div className="equations">
              <div className="equation">CaO + 2HNO₃ → Ca(NO3)2 + H₂O</div>
              <div className="equation blue">CuO + 2HNO₃ → Cu(NO3)2 + H₂O</div>
            </div>
            <p>
              Metal oxides act as bases - they react with acids to form salts and water.
              The blue color of Cu(NO3)2 is characteristic of Cu²⁺ ions in solution.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .oxide-containers {
            display: flex;
            gap: 10px;
          }

          .oxide-container {
            padding: 5px;
            border-radius: 4px;
            transition: all 0.2s;
          }

          .oxide-container.clickable {
            cursor: pointer;
          }

          .oxide-container.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .test-tubes {
            display: flex;
            gap: 15px;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .results-summary {
            margin-top: 16px;
            padding: 12px;
            background: var(--bg-elevated);
            border-radius: 6px;
          }

          .results-summary h4 {
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 0 0 10px 0;
            font-size: 12px;
            color: var(--text-primary);
          }

          .result-row {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 4px 0;
            font-size: 11px;
            color: var(--text-secondary);
          }

          .result-row .swatch {
            width: 16px;
            height: 16px;
            border-radius: 3px;
            border: 1px solid rgba(255,255,255,0.2);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.reaction {
            background: rgba(0, 102, 204, 0.2);
            color: ${saltFormationColors.cuNO32};
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .equations {
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
          }

          .equation {
            font-family: 'Courier New', monospace;
            color: #888;
            padding: 4px 0;
          }

          .equation.blue {
            color: ${saltFormationColors.cuNO32};
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
